import {
    createElement,
    useCallback,
    useEffect,
    useId,
    useRef,
    useState,
} from "react";
import { useTranslation } from "react-i18next";
import { View } from "react-native";

import { toastError } from "@/components/AppToast";
import { Text } from "../../components/ui/text";
import { AttachmentFileList } from "./AttachmentFileList";
import {
    DropzoneSurface,
    mergeAttachments,
    revokeAttachmentUri,
} from "./attachmentPickerShared";
import {
    ACCEPTED_ATTACHMENT_MIME_TYPES,
    createAttachmentId,
    MAX_ATTACHMENTS,
    resolveAttachmentMimeType,
    type AttachmentFile,
} from "./attachmentUtils";

type AttachmentPickerProps = {
    files: AttachmentFile[];
    onChange: (files: AttachmentFile[]) => void;
    maxFiles?: number;
};

type DragEventLike = {
    preventDefault: () => void;
    stopPropagation: () => void;
    dataTransfer?: DataTransfer | null;
};

function mapFileList(fileList: FileList): AttachmentFile[] {
    return Array.from(fileList).map((file) => ({
        id: createAttachmentId(file.name, file.size),
        name: file.name,
        size: file.size,
        mimeType:
            resolveAttachmentMimeType(file.type || null, file.name) ??
            (file.type || null),
        uri: URL.createObjectURL(file),
    }));
}

export function AttachmentPicker({
    files,
    onChange,
    maxFiles = MAX_ATTACHMENTS,
}: AttachmentPickerProps) {
    const { t } = useTranslation();
    const inputId = useId();
    const filesRef = useRef(files);
    const [dragDepth, setDragDepth] = useState(0);
    const isDragging = dragDepth > 0;
    const remainingSlots = Math.max(maxFiles - files.length, 0);
    const isDisabled = remainingSlots === 0;

    useEffect(() => {
        filesRef.current = files;
    }, [files]);

    useEffect(() => {
        return () => {
            for (const file of filesRef.current) {
                revokeAttachmentUri(file.uri);
            }
        };
    }, []);

    // Prevent the browser from navigating to a dropped file (default drop behavior).
    useEffect(() => {
        function preventWindowFileDrop(event: DragEvent) {
            event.preventDefault();
        }

        window.addEventListener("dragover", preventWindowFileDrop);
        window.addEventListener("drop", preventWindowFileDrop);

        return () => {
            window.removeEventListener("dragover", preventWindowFileDrop);
            window.removeEventListener("drop", preventWindowFileDrop);
        };
    }, []);

    const addFiles = useCallback(
        (fileList: FileList | null) => {
            if (!fileList || fileList.length === 0 || isDisabled) {
                return;
            }

            const incoming = mapFileList(fileList);
            const merged = mergeAttachments(files, incoming, maxFiles);

            if (merged.length === files.length) {
                toastError(
                    t("leave.attachments.label"),
                    t("leave.dropzone.duplicateOrLimit"),
                );
                return;
            }

            onChange(merged);
        },
        [files, isDisabled, maxFiles, onChange, t],
    );

    const handleRemove = useCallback(
        (id: string) => {
            const removed = files.find((file) => file.id === id);

            if (removed) {
                revokeAttachmentUri(removed.uri);
            }

            onChange(files.filter((file) => file.id !== id));
        },
        [files, onChange],
    );

    const openFileDialog = useCallback(() => {
        if (isDisabled) {
            return;
        }

        const input = document.getElementById(inputId);
        if (input instanceof HTMLInputElement) {
            input.click();
        }
    }, [inputId, isDisabled]);

    const handleDragEnter = useCallback(
        (event: DragEventLike) => {
            event.preventDefault();
            event.stopPropagation();
            if (!isDisabled) {
                setDragDepth((depth) => depth + 1);
            }
        },
        [isDisabled],
    );

    const handleDragOver = useCallback(
        (event: DragEventLike) => {
            event.preventDefault();
            event.stopPropagation();
            if (event.dataTransfer) {
                event.dataTransfer.dropEffect = isDisabled ? "none" : "copy";
            }
        },
        [isDisabled],
    );

    const handleDragLeave = useCallback((event: DragEventLike) => {
        event.preventDefault();
        event.stopPropagation();
        setDragDepth((depth) => Math.max(depth - 1, 0));
    }, []);

    const handleDrop = useCallback(
        (event: DragEventLike) => {
            event.preventDefault();
            event.stopPropagation();
            setDragDepth(0);
            addFiles(event.dataTransfer?.files ?? null);
        },
        [addFiles],
    );

    return (
        <View className="gap-3">
            {remainingSlots > 0 && (
                <View className="gap-2">
                    {createElement(
                        "div",
                        {
                            onDragEnter: handleDragEnter,
                            onDragOver: handleDragOver,
                            onDragLeave: handleDragLeave,
                            onDrop: handleDrop,
                        },
                        createElement(DropzoneSurface, {
                            isDragging,
                            isDisabled,
                            onPress: openFileDialog,
                            remainingSlots,
                        }),
                    )}

                    {createElement("input", {
                        id: inputId,
                        type: "file",
                        multiple: true,
                        accept: ACCEPTED_ATTACHMENT_MIME_TYPES.join(","),
                        style: { display: "none" },
                        onChange: (event: Event) => {
                            const target = event.target as HTMLInputElement;
                            addFiles(target.files);
                            target.value = "";
                        },
                    })}
                    <Text variant="caption">{t("leave.attachments.hint")}</Text>
                </View>
            )}

            <AttachmentFileList files={files} onRemove={handleRemove} />
        </View>
    );
}
